import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiBookOpen, 
  FiClock, 
  FiStar, 
  FiPlay, 
  FiCheckCircle, 
  FiSettings,
  FiBarChart2
} from 'react-icons/fi';
import axios from 'axios';
import dashboardEvents from '../utils/dashboardEvents';
import PurchaseNotification from '../components/ui/PurchaseNotification';
import { useAuth } from '../contexts/AuthContext';

const StudentDashboard = () => {
  const { refreshUser } = useAuth();
  const [stats, setStats] = useState({
    totalCourses: 0,
    completedCourses: 0,
    totalHours: 0,
    averageGrade: 0
  });
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [recommendedCourses, setRecommendedCourses] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchaseNotification, setPurchaseNotification] = useState({
    isVisible: false,
    courses: []
  });

  const fetchDashboardData = useCallback(async () => {
    try {
      console.log('Fetching dashboard data with optimized endpoint...');
      
      // Single optimized API call that gets all dashboard data
      const response = await axios.get('/api/courses/dashboard-data');
      console.log('Dashboard data response:', response.data);
      
      // Set all data from the single response
      setEnrolledCourses(response.data.enrolledCourses || []);
      setRecommendedCourses(response.data.recommendedCourses || []);
      setRecentActivity(response.data.recentActivity || []);
      setStats(response.data.stats || {
        totalCourses: 0,
        completedCourses: 0,
        totalHours: 0,
        averageGrade: 0
      });
      
      console.log('Dashboard data loaded successfully');
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setEnrolledCourses([]);
      setRecommendedCourses([]);
      setRecentActivity([]);
      setStats({
        totalCourses: 0,
        completedCourses: 0,
        totalHours: 0,
        averageGrade: 0
      });
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
    
    // Listen for course purchase events
    const handleCoursePurchased = async (data) => {
      console.log('Course purchased, refreshing dashboard data:', data);
      
      // Refresh user data first
      await refreshUser();
      
      // Then refresh dashboard data
      fetchDashboardData();
      
      // Show purchase notification
      setPurchaseNotification({
        isVisible: true,
        courses: data.courses
      });
    };
    
    const handleEnrollmentAdded = async (data) => {
      console.log('Enrollment added, refreshing dashboard data:', data);
      
      // Refresh user data first
      await refreshUser();
      
      // Then refresh dashboard data
      fetchDashboardData();
    };
    
    dashboardEvents.on('course-purchased', handleCoursePurchased);
    dashboardEvents.on('enrollment-added', handleEnrollmentAdded);
    
    // Cleanup listeners on unmount
    return () => {
      dashboardEvents.off('course-purchased', handleCoursePurchased);
      dashboardEvents.off('enrollment-added', handleEnrollmentAdded);
    };
  }, [fetchDashboardData, refreshUser]);

  const quickActions = [
    {
      title: '계속 학습하기',
      description: '진행 중인 강의를 이어서 학습하세요',
      icon: FiPlay,
      link: '/my-courses',
      color: 'bg-primary-600 hover:bg-primary-700'
    },
    {
      title: '새 강의 찾기',
      description: '관심 있는 강의를 탐색하세요',
      icon: FiBookOpen,
      link: '/courses',
      color: 'bg-green-600 hover:bg-green-700'
    },
    {
      title: '학습 통계',
      description: '학습 진행 상황을 확인하세요',
      icon: FiBarChart2,
      link: '/learning-stats',
      color: 'bg-blue-600 hover:bg-blue-700'
    },
    {
      title: '프로필 설정',
      description: '개인 정보를 관리하세요',
      icon: FiSettings,
      link: '/profile',
      color: 'bg-gray-600 hover:bg-gray-700'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="loading-spinner mx-auto"></div>
            <p className="mt-4 text-gray-600">대시보드를 불러오는 중...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Purchase Notification */}
      <PurchaseNotification
        isVisible={purchaseNotification.isVisible}
        courses={purchaseNotification.courses}
        onClose={() => setPurchaseNotification({ isVisible: false, courses: [] })}
      />
      
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">학습 대시보드</h1>
              <p className="text-gray-600 mt-1">학습 진행 상황과 추천 강의를 확인하세요</p>
            </div>
            <Link
              to="/courses"
              className="bg-primary-800 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors duration-200 flex items-center space-x-2"
            >
              <FiBookOpen className="h-5 w-5" />
              <span>새 강의 찾기</span>
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-soft p-6">
            <div className="flex items-center">
              <div className="p-3 bg-primary-100 rounded-lg">
                <FiBookOpen className="h-6 w-6 text-primary-800" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">수강 중인 강의</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalCourses}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-soft p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <FiCheckCircle className="h-6 w-6 text-green-800" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">완료한 강의</p>
                <p className="text-2xl font-bold text-gray-900">{stats.completedCourses}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-soft p-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg">
                <FiClock className="h-6 w-6 text-blue-800" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">총 학습 시간</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalHours}시간</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-soft p-6">
            <div className="flex items-center">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <FiStar className="h-6 w-6 text-yellow-800" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">평균 평점</p>
                <p className="text-2xl font-bold text-gray-900">{stats.averageGrade}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Quick Actions */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-soft p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">빠른 작업</h2>
              <div className="space-y-3">
                {quickActions.map((action, index) => (
                  <Link
                    key={index}
                    to={action.link}
                    className={`flex items-center p-4 rounded-lg text-white transition-colors duration-200 ${action.color}`}
                  >
                    <action.icon className="h-5 w-5 mr-3" />
                    <div>
                      <p className="font-medium">{action.title}</p>
                      <p className="text-sm opacity-90">{action.description}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Enrolled Courses */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-soft p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">내 강의</h2>
                <div className="flex items-center space-x-4">
                  <Link
                    to="/my-courses"
                    className="text-primary-800 hover:text-primary-600 font-medium"
                  >
                    모든 강의 보기
                  </Link>
                </div>
              </div>
              <div className="space-y-4">
                {enrolledCourses.length === 0 ? (
                  <div className="text-center py-8 text-gray-600">
                    <FiBookOpen className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <p>수강 중인 강의가 없습니다.</p>
                    <Link
                      to="/courses"
                      className="inline-block mt-2 text-primary-800 hover:text-primary-600 font-medium"
                    >
                      강의 탐색하기
                    </Link>
                  </div>
                ) : (
                  enrolledCourses.map(enrollment => (
                    <div key={enrollment._id} className="flex items-center space-x-4 p-4 border border-gray-200 rounded-lg">
                    <img
                      src={enrollment.course?.thumbnail || '/images/default-course.svg'}
                      alt={enrollment.course?.title}
                      className="w-16 h-12 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-semibold text-gray-900">{enrollment.course?.title}</h3>
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
                        <span className="flex items-center">
                          <img
                            src={enrollment.course?.createdBy?.avatar || '/images/default-avatar.svg'}
                            alt={enrollment.course?.createdBy?.name || 'Unknown'}
                            className="w-4 h-4 rounded-full mr-1"
                          />
                          {enrollment.course?.createdBy?.name || 'Unknown'}
                        </span>
                        <span className="flex items-center">
                          <FiClock className="h-4 w-4 mr-1" />
                          {enrollment.course?.totalDuration || 0}분
                        </span>
                        <span className="flex items-center">
                          <FiStar className="h-4 w-4 mr-1 text-yellow-500" />
                          {enrollment.course?.averageRating || 0}
                        </span>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="flex-1">
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-gray-600">진행률</span>
                            <span className="font-medium">{enrollment.progress}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${enrollment.progress}%` }}
                            ></div>
                          </div>
                        </div>
                        <Link
                          to={enrollment.lastViewedLesson 
                            ? `/courses/${enrollment.course?._id}/learn?unitIndex=${enrollment.lastViewedLesson.unitIndex}&lessonIndex=${enrollment.lastViewedLesson.lessonIndex}`
                            : `/courses/${enrollment.course?._id}/learn`
                          }
                          className="px-4 py-2 bg-primary-800 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 text-sm"
                        >
                          계속 학습
                        </Link>
                      </div>
                    </div>
                  </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="mt-8">
          <div className="bg-white rounded-lg shadow-soft p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">최근 활동</h2>
              <Link
                to="/activity"
                className="text-primary-800 hover:text-primary-600 font-medium"
              >
                모든 활동 보기
              </Link>
            </div>
            <div className="space-y-4">
              {recentActivity.filter(activity => activity.course && activity.course._id).map((activity, index) => (
                <div key={index} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                  <div className="p-2 rounded-lg bg-blue-100">
                    <FiPlay className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{activity.message}</p>
                    <p className="text-sm text-gray-600">{activity.course?.title}</p>
                  </div>
                  <span className="text-sm text-gray-500">
                    {new Date(activity.lastActivity).toLocaleDateString('ko-KR')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recommended Courses */}
        <div className="mt-8">
          <div className="bg-white rounded-lg shadow-soft p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">추천 강의</h2>
              <Link
                to="/courses"
                className="text-primary-800 hover:text-primary-600 font-medium"
              >
                더 많은 강의 보기
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {recommendedCourses.map(course => (
                <div key={course._id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow duration-200">
                  <img
                    src={course.thumbnail || '/images/default-course.svg'}
                    alt={course.title}
                    className="w-full h-32 object-cover"
                  />
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-2">{course.title}</h3>
                    <div className="flex items-center space-x-2 mb-2">
                      <img
                        src={course.createdBy?.avatar || '/images/default-avatar.svg'}
                        alt={course.createdBy?.name || 'Unknown'}
                        className="w-5 h-5 rounded-full"
                      />
                      <span className="text-sm text-gray-600">{course.createdBy?.name || 'Unknown'}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm text-gray-600 mb-3">
                      <span className="flex items-center">
                        <FiStar className="h-4 w-4 mr-1 text-yellow-500" />
                        {course.averageRating || 0}
                      </span>
                      <span>{course.enrolledStudents || 0}명 수강</span>
                      <span>{course.totalDuration || 0}분</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-lg font-bold text-primary-800">₩{course.price?.toLocaleString() || 0}</span>
                        {course.originalPrice > course.price && (
                          <span className="text-sm text-gray-500 line-through">₩{course.originalPrice?.toLocaleString()}</span>
                        )}
                      </div>
                      <Link
                        to={`/courses/${course._id}`}
                        className="px-4 py-2 bg-primary-800 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 text-sm"
                      >
                        자세히 보기
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard; 